import React, { useContext, useMemo, useState } from "react";
import "./TorenControls.css";
import { CaiGenerationEntitlementContext } from "./CaiGenerationEntitlementContext";
import {
  buildContentBriefGenerationModel,
} from "../../lib/currentAssetIntelligence/contentBriefGenerationModel";
import {
  canGenerateContentBrief,
  recordSuccessfulContentBriefGeneration,
  resolveContentBriefQuota,
} from "../../lib/currentAssetIntelligence/contentBriefQuotaModel";
import CaiDurableJobPanel from "./CaiDurableJobPanel";

type ContentBriefRecord = {
  title: string;
  accountName: string;
  recommendationTitle: string;
  outputType: string;
  targetAsset: string;
  targetAudience: string;
  buyerIntent: string | null;
  localIntent: string | null;
  evidencePattern: string;
  objective: string;
  expectedResult: string;
  suggestedAngle: string;
  outline: string[];
  recommendedSections: string[];
  FAQsToAnswer: string[];
  claimsToSupport: string[];
  proofPointsNeeded: string[];
  schemaOrPageNotes: string[];
  internalNotesExcludedFromCustomerView: boolean;
  generatedAt: string | null;
  idempotencyKey: string;
};

type Props = {
  accountId: string;
  runId?: string | null;
  recommendationId?: string | null;
  recommendationKey?: string | null;
  recommendationType?: string | null;
  entityArea?: string | null;
  targetEntity?: string | null;
  recommendedOutput?: string | null;
  confidence?: string | number | null;
  nextStep?: string | null;
  actionBriefId?: string | null;
  actionBriefReady: boolean;
  liveCaiEnabled?: boolean;
  caiRunEligible?: boolean;
  opportunityCategory?: "asset" | "entity" | "competitive" | null;
  accountType?: string | null;
  entitlementOverride?: unknown;
  planDefault?: unknown;
  productArea: "local" | "competitive";
  accountName?: string | null;
  recommendationTitle: string;
  targetAsset: string;
  targetAudience: string;
  evidencePattern: string;
  objective: string;
  expectedResult: string;
};

function briefText(result: ContentBriefRecord): string {
  return [
    result.title,
    "",
    `Account: ${result.accountName}`,
    `Recommendation: ${result.recommendationTitle}`,
    `Output: ${result.outputType}`,
    `Target asset: ${result.targetAsset}`,
    `Audience: ${result.targetAudience}`,
    result.buyerIntent ? `Buyer intent: ${result.buyerIntent}` : `Local intent: ${result.localIntent}`,
    `Evidence pattern: ${result.evidencePattern}`,
    `Objective: ${result.objective}`,
    `Expected result: ${result.expectedResult}`,
    "",
    "Suggested angle",
    result.suggestedAngle,
    "",
    "Outline",
    ...result.outline.map((item) => `- ${item}`),
    "",
    "FAQs to answer",
    ...result.FAQsToAnswer.map((item) => `- ${item}`),
    "",
    "Claims to support",
    ...result.claimsToSupport.map((item) => `- ${item}`),
    "",
    "Proof points needed",
    ...result.proofPointsNeeded.map((item) => `- ${item}`),
  ].join("\n");
}

export default function ContentBriefAction({
  accountId,
  runId,
  recommendationId,
  recommendationKey,
  recommendationType,
  entityArea,
  targetEntity,
  recommendedOutput,
  confidence,
  nextStep,
  actionBriefId,
  actionBriefReady,
  liveCaiEnabled = process.env.NODE_ENV !== "test",
  caiRunEligible = true,
  opportunityCategory,
  accountType,
  entitlementOverride,
  planDefault,
  productArea,
  accountName,
  recommendationTitle,
  targetAsset,
  targetAudience,
  evidencePattern,
  objective,
  expectedResult,
}: Props) {
  const generationEnabled = useContext(CaiGenerationEntitlementContext);
  const [generated, setGenerated] = useState<ContentBriefRecord | null>(null);
  const [copied, setCopied] = useState(false);
  const [usedKeys, setUsedKeys] = useState<string[]>([]);
  const quota = useMemo(() => resolveContentBriefQuota({ accountType, entitlementOverride, planDefault, used: usedKeys.length }), [accountType, entitlementOverride, planDefault, usedKeys.length]);
  const ready = canGenerateContentBrief(quota, actionBriefReady);
  if (liveCaiEnabled) {
    const category = opportunityCategory || (entityArea ? "entity" : productArea === "local" ? "asset" : "competitive");
    if (category !== "asset") return <p className="oe-governed-next-step" data-testid="oe-category-action-boundary">Use the existing governed tools for this opportunity. This category does not generate CAI implementation briefs.</p>;
    return <div data-testid="content-brief-action" data-testid-durable="true" style={{ marginTop: 9, paddingTop: 9, borderTop: "1px solid #dbeafe" }}>
      <CaiDurableJobPanel
        generationEnabled={generationEnabled}
        enabled={Boolean(accountId && caiRunEligible)}
        accountName={accountName || "Selected account"}
        opportunity={{
          accountId,
          accountType,
          runId,
          recommendationId,
          recommendationKey: recommendationKey || recommendationId || null,
          recommendationSourceKey: recommendationKey || recommendationId || null,
          recommendationType,
          entityArea,
          targetEntity,
          recommendedOutput,
          targetAsset,
          targetAudience,
          confidence,
          evidencePattern,
          expectedResult,
          nextStep,
          actionBriefId,
          productArea,
          recommendationTitle,
          opportunityCategory: opportunityCategory || (entityArea ? "entity" : productArea === "local" ? "asset" : "competitive"),
        }}
      />
    </div>;
  }
  const actionLabel = !actionBriefReady
    ? "Requires Action Brief first"
    : quota.status === "needs_configuration"
      ? quota.message
      : quota.status === "exhausted"
        ? "Monthly Content Brief limit reached"
        : quota.status === "disabled"
          ? quota.message
          : "Generate Content Brief";

  const handleGenerate = () => {
    if (!ready) return;
    const result = buildContentBriefGenerationModel({
      accountId,
      runId,
      recommendationId,
      actionBriefId,
      actionBriefReady,
      productArea,
      accountName,
      recommendationTitle,
      targetAsset,
      targetAudience,
      evidencePattern,
      objective,
      expectedResult,
      generatedAt: new Date().toISOString(),
    });
    if (result.status !== "ready" || !result.contentBrief) return;
    const counted = recordSuccessfulContentBriefGeneration(quota, result.idempotencyKey, usedKeys);
    if (!counted.counted) return;
    setUsedKeys((current) => [...current, result.idempotencyKey]);
    setGenerated(result.contentBrief);
    setCopied(false);
  };

  const copyBrief = async () => {
    if (!generated) return;
    await navigator.clipboard?.writeText(briefText(generated));
    setCopied(true);
  };

  return <div data-testid="content-brief-action" style={{ marginTop: 9, paddingTop: 9, borderTop: "1px solid #dbeafe" }}>
    <div style={{ display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
      <div>
        <div style={{ color: "#64748b", fontSize: 11, fontWeight: 800, textTransform: "uppercase" }}>Content Brief</div>
        <div data-testid="content-brief-quota" style={{ marginTop: 3, color: "#64748b", fontSize: 11 }}>{quota.limit === null ? quota.message : `Content Briefs: ${quota.used} of ${quota.limit} used this month`}</div>
      </div>
      <button type="button" disabled={!ready} onClick={handleGenerate} title={actionLabel} className="toren-control toren-control--secondary">{actionLabel}</button>
    </div>
    {generated ? <section data-testid="content-brief-output" style={{ marginTop: 9, padding: 10, border: "1px solid #bfdbfe", borderRadius: 10, background: "#ffffff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
        <strong style={{ color: "#172554", fontSize: 13 }}>{generated.title}</strong>
        <button type="button" onClick={copyBrief} className="toren-control toren-control--utility">{copied ? "✓ Copied" : "Copy"}</button>
      </div>
      <div style={{ marginTop: 7, color: "#475569", fontSize: 12, lineHeight: 1.45 }}><strong>Audience:</strong> {generated.targetAudience}<br /><strong>Evidence:</strong> {generated.evidencePattern}<br /><strong>Expected result:</strong> {generated.expectedResult}</div>
      <ul style={{ margin: "7px 0 0", paddingLeft: 18, color: "#475569", fontSize: 12 }}>{generated.outline.slice(0, 6).map((item) => <li key={item}>{item}</li>)}</ul>
      <div style={{ marginTop: 7, color: "#64748b", fontSize: 10 }}>Local preview; account usage enforcement requires the account ledger.</div>
    </section> : null}
  </div>;
}
